import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Check, RefreshCw, Zap, FileText, Receipt, Building2,
    ArrowRight, Edit2, Trash2, Plus, CheckCircle2, Clock, Search
} from 'lucide-react';
import { useCallback } from 'react';

export default function Reconciliation() {
    const { API } = useContext(AuthContext);

    // Matches and pending items
    const [matches, setMatches] = useState([]);
    const [pendingItems, setPendingItems] = useState({ inbox: [], purchases: [], sales: [], bank_movements: [] });
    const [loading, setLoading] = useState(true);
    const [autoMatching, setAutoMatching] = useState(false);

    // Edit dialog
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);
    const [editedProposal, setEditedProposal] = useState(null);

    // AI Generation
    const [generating, setGenerating] = useState({});
    const [userDescription, setUserDescription] = useState({});

    // Manual match selection
    const [selectedInbox, setSelectedInbox] = useState(null);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [selectedBank, setSelectedBank] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [matchesRes, pendingRes] = await Promise.all([
                axios.get(`${API}/reconciliation/matches?status=pending`),
                axios.get(`${API}/reconciliation/pending-items`)
            ]);
            setMatches(matchesRes.data || []);
            setPendingItems(pendingRes.data || { inbox: [], purchases: [], sales: [], bank_movements: [] });
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    }, [API]);

    useEffect(() => {
        loadData();
    }, [loadData]);



    const runAutoMatch = async () => {
        setAutoMatching(true);
        try {
            const res = await axios.post(`${API}/reconciliation/auto-match`);
            toast.success(`Se crearon ${res.data.matches_created} propuestas de match`);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error en auto-match');
        } finally {
            setAutoMatching(false);
        }
    };

    const confirmMatch = async (matchId) => {
        try {
            await axios.post(`${API}/reconciliation/matches/${matchId}/confirm`);
            toast.success('Match confirmado, asiento creado');
            loadData();
        } catch (error) {
            toast.error('Error al confirmar');
        }
    };

    const deleteMatch = async (matchId) => {
        try {
            await axios.delete(`${API}/reconciliation/matches/${matchId}`);
            toast.success('Propuesta eliminada');
            loadData();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const generateAIProposal = async (matchId) => {
        const desc = userDescription[matchId];
        if (!desc) {
            toast.error('Ingresa una descripción para ayudar a la IA');
            return;
        }

        setGenerating(prev => ({ ...prev, [matchId]: true }));
        try {
            await axios.post(`${API}/reconciliation/matches/${matchId}/generate-proposal`, {
                user_description: desc
            });
            toast.success('Propuesta generada por IA');
            loadData();
        } catch (error) {
            toast.error('Error al generar propuesta');
        } finally {
            setGenerating(prev => ({ ...prev, [matchId]: false }));
        }
    };

    const openEditDialog = (match) => {
        setEditingMatch(match);
        setEditedProposal(match.proposed_entry || { description: '', date: '', entries: [] });
        setShowEditDialog(true);
    };

    const saveEdit = async () => {
        if (!editingMatch) return;
        try {
            await axios.put(`${API}/reconciliation/matches/${editingMatch.id}`, {
                proposed_entry: editedProposal
            });
            toast.success('Propuesta actualizada');
            setShowEditDialog(false);
            loadData();
        } catch (error) {
            toast.error('Error al guardar');
        }
    };

    const createManualMatch = async () => {
        if (!selectedInbox && !selectedPurchase && !selectedBank) {
            toast.error('Selecciona al menos un elemento');
            return;
        }
        try {
            const params = new URLSearchParams();
            if (selectedInbox) params.append('inbox_doc_id', selectedInbox.id);
            if (selectedPurchase) params.append('purchase_book_id', selectedPurchase.id);
            if (selectedBank) params.append('bank_movement_id', selectedBank.id);

            await axios.post(`${API}/reconciliation/create-match?${params.toString()}`);
            toast.success('Match manual creado');
            setSelectedInbox(null);
            setSelectedPurchase(null);
            setSelectedBank(null);
            loadData();
        } catch (error) {
            toast.error('Error al crear match');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency', currency: 'CLP', minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const updateEntry = (index, field, value) => {
        const newEntries = [...(editedProposal.entries || [])];
        newEntries[index] = { ...newEntries[index], [field]: value };
        setEditedProposal({ ...editedProposal, entries: newEntries });
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold">Conciliación</h1>
                    <p className="text-muted-foreground">Vincula documentos, facturas SII y movimientos bancarios.</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={runAutoMatch} disabled={autoMatching} className="bg-blue-600 hover:bg-blue-700">
                        <Zap className={`h-4 w-4 mr-2 ${autoMatching ? 'animate-pulse' : ''}`} />
                        {autoMatching ? 'Procesando...' : 'Auto-Match'}
                    </Button>
                    <Button variant="outline" onClick={loadData} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Recargar
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6 flex items-center gap-3">
                        <Clock className="h-8 w-8 text-yellow-500" />
                        <div>
                            <div className="text-2xl font-bold">{matches.length}</div>
                            <p className="text-sm text-muted-foreground">Propuestas por revisar</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 flex items-center gap-3">
                        <FileText className="h-8 w-8 text-blue-500" />
                        <div>
                            <div className="text-2xl font-bold">{pendingItems.inbox?.length || 0}</div>
                            <p className="text-sm text-muted-foreground">Docs. pendientes</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 flex items-center gap-3">
                        <Receipt className="h-8 w-8 text-green-500" />
                        <div>
                            <div className="text-2xl font-bold">{pendingItems.purchases?.length || 0}</div>
                            <p className="text-sm text-muted-foreground">Facturas pendientes</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-purple-500" />
                        <div>
                            <div className="text-2xl font-bold">{pendingItems.bank_movements?.length || 0}</div>
                            <p className="text-sm text-muted-foreground">Movimientos pendientes</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="proposals" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="proposals">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Propuestas ({matches.length})
                    </TabsTrigger>
                    <TabsTrigger value="manual">
                        <Search className="h-4 w-4 mr-2" />
                        Match Manual
                    </TabsTrigger>
                </TabsList>

                {/* Proposals Tab */}
                <TabsContent value="proposals" className="space-y-4">
                    {matches.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center text-muted-foreground">
                                <Zap className="h-12 w-12 mx-auto mb-4 opacity-30" />
                                <p>No hay propuestas de match.</p>
                                <p className="text-sm">Usa "Auto-Match" para generar propuestas automáticamente.</p>
                            </CardContent>
                        </Card>
                    ) : (
                        matches.map((match) => (
                            <Card key={match.id} className="border-l-4 border-l-yellow-500">
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-4">
                                        {/* Inbox Doc */}
                                        <div className="flex-1 p-3 bg-blue-50 rounded-lg">
                                            <div className="text-xs font-medium text-blue-700 mb-1">📄 Bandeja</div>
                                            {match.inbox ? (
                                                <>
                                                    <div className="font-medium text-sm truncate">{match.inbox.text_content?.slice(0, 50)}...</div>
                                                    <div className="text-xs text-muted-foreground">{match.inbox.date || match.inbox.created_at?.slice(0, 10)}</div>
                                                </>
                                            ) : <span className="text-sm text-muted-foreground">No vinculado (Ayuda a la IA)</span>}
                                        </div>

                                        <ArrowRight className="h-4 w-4 mt-4 text-muted-foreground flex-shrink-0" />

                                        {/* Purchase Book */}
                                        <div className="flex-1 p-3 bg-green-50 rounded-lg">
                                            <div className="text-xs font-medium text-green-700 mb-1">📋 Factura SII</div>
                                            {match.purchase ? (
                                                <>
                                                    <div className="font-medium text-sm truncate">{match.purchase.razon_social}</div>
                                                    <div className="text-xs">{formatCurrency(match.purchase.monto_total)}</div>
                                                    <div className="text-xs text-muted-foreground">Folio: {match.purchase.folio}</div>
                                                </>
                                            ) : <span className="text-sm text-muted-foreground">No vinculado</span>}
                                        </div>

                                        <ArrowRight className="h-4 w-4 mt-4 text-muted-foreground flex-shrink-0" />

                                        {/* Bank Movement */}
                                        <div className="flex-1 p-3 bg-purple-50 rounded-lg">
                                            <div className="text-xs font-medium text-purple-700 mb-1">🏦 Banco</div>
                                            {match.bank ? (
                                                <>
                                                    <div className="font-medium text-sm truncate">{match.bank.description}</div>
                                                    <div className="text-xs">{formatCurrency(match.bank.debit || match.bank.credit)}</div>
                                                    <div className="text-xs text-muted-foreground">{match.bank.date}</div>
                                                </>
                                            ) : <span className="text-sm text-muted-foreground">No vinculado</span>}
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                            <Button size="sm" onClick={() => openEditDialog(match)} variant="outline">
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                            <Button size="sm" onClick={() => confirmMatch(match.id)} className="bg-green-600 hover:bg-green-700" disabled={!match.proposed_entry?.entries?.length}>
                                                <Check className="h-4 w-4" />
                                            </Button>
                                            <Button size="sm" onClick={() => deleteMatch(match.id)} variant="ghost" className="text-red-500">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Proposal Preview or AI Generation Input */}
                                    {match.proposed_entry?.entries?.length > 0 ? (
                                        <div className="mt-3 pt-3 border-t">
                                            <div className="text-xs font-medium mb-2">Asiento propuesto:</div>
                                            <div className="rounded-md border overflow-hidden mt-2">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase">
                                                        <tr>
                                                            <th className="h-8 px-4 text-left font-medium">Código</th>
                                                            <th className="h-8 px-4 text-left font-medium">Cuenta</th>
                                                            <th className="h-8 px-4 text-right font-medium">Debe</th>
                                                            <th className="h-8 px-4 text-right font-medium">Haber</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {match.proposed_entry.entries.map((entry, i) => (
                                                            <tr key={i} className="border-t hover:bg-muted/50 transition-colors">
                                                                <td className="p-2 px-4 font-mono text-xs text-muted-foreground">{entry.account_code}</td>
                                                                <td className="p-2 px-4 font-medium">{entry.account_name}</td>
                                                                <td className="p-2 px-4 text-right text-blue-600 font-medium">
                                                                    {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                                                                </td>
                                                                <td className="p-2 px-4 text-right text-red-600 font-medium">
                                                                    {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        <tr className="border-t bg-secondary/30 font-bold text-xs">
                                                            <td colSpan={2} className="p-2 px-4 uppercase text-muted-foreground">Totales</td>
                                                            <td className="p-2 px-4 text-right text-blue-700">
                                                                {formatCurrency(match.proposed_entry.entries.reduce((s, e) => s + (e.debit || 0), 0))}
                                                            </td>
                                                            <td className="p-2 px-4 text-right text-red-700">
                                                                {formatCurrency(match.proposed_entry.entries.reduce((s, e) => s + (e.credit || 0), 0))}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mt-3 pt-3 border-t bg-yellow-50 p-3 rounded-lg">
                                            <div className="flex gap-4 items-end">
                                                <div className="flex-1">
                                                    <label className="text-xs font-medium text-yellow-800 mb-1 block">
                                                        ⚠️ Match incompleto: Falta el documento. Ayuda a la IA:
                                                    </label>
                                                    <Input
                                                        placeholder="¿De qué es este gasto? Ej: Servicio de alarmas, Compra de insumos..."
                                                        className="bg-white"
                                                        value={userDescription[match.id] || ''}
                                                        onChange={(e) => setUserDescription({ ...userDescription, [match.id]: e.target.value })}
                                                    />
                                                </div>
                                                <Button
                                                    onClick={() => generateAIProposal(match.id)}
                                                    disabled={generating[match.id]}
                                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                                >
                                                    <Zap className="h-4 w-4 mr-2" />
                                                    {generating[match.id] ? 'Generando...' : 'Generar Asiento con IA'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))
                    )}
                </TabsContent>

                {/* Manual Match Tab */}
                <TabsContent value="manual">
                    <div className="grid grid-cols-3 gap-4">
                        {/* Inbox Column */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <FileText className="h-4 w-4" /> Bandeja de Entrada
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="max-h-[400px] overflow-y-auto space-y-2">
                                {pendingItems.inbox?.map((doc) => (
                                    <div
                                        key={doc.id}
                                        onClick={() => setSelectedInbox(selectedInbox?.id === doc.id ? null : doc)}
                                        className={`p-2 border rounded cursor-pointer text-sm ${selectedInbox?.id === doc.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-secondary/50'
                                            }`}
                                    >
                                        <div className="truncate">{doc.text_content?.slice(0, 40)}...</div>
                                        <div className="text-xs text-muted-foreground">{doc.date || doc.created_at?.slice(0, 10)}</div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Purchase Book Column */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Receipt className="h-4 w-4" /> Libro de Compras
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="max-h-[400px] overflow-y-auto space-y-2">
                                {pendingItems.purchases?.map((purchase) => (
                                    <div
                                        key={purchase.id}
                                        onClick={() => setSelectedPurchase(selectedPurchase?.id === purchase.id ? null : purchase)}
                                        className={`p-2 border rounded cursor-pointer text-sm ${selectedPurchase?.id === purchase.id ? 'border-green-500 bg-green-50' : 'hover:bg-secondary/50'
                                            }`}
                                    >
                                        <div className="truncate font-medium">{purchase.razon_social}</div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-muted-foreground">F: {purchase.folio}</span>
                                            <span className="text-xs font-medium">{formatCurrency(purchase.monto_total)}</span>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Bank Column */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Building2 className="h-4 w-4" /> Movimientos Banco
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="max-h-[400px] overflow-y-auto space-y-2">
                                {pendingItems.bank_movements?.map((mov) => (
                                    <div
                                        key={mov.id}
                                        onClick={() => setSelectedBank(selectedBank?.id === mov.id ? null : mov)}
                                        className={`p-2 border rounded cursor-pointer text-sm ${selectedBank?.id === mov.id ? 'border-purple-500 bg-purple-50' : 'hover:bg-secondary/50'
                                            }`}
                                    >
                                        <div className="truncate">{mov.description}</div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-muted-foreground">{mov.date}</span>
                                            <span className={`text-xs font-medium ${mov.debit ? 'text-red-600' : 'text-green-600'}`}>
                                                {formatCurrency(mov.debit || mov.credit)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Selection Summary */}
                    <Card className="mt-4">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm">
                                <span className={selectedInbox ? 'text-blue-600 font-medium' : 'text-muted-foreground'}>
                                    {selectedInbox ? '✓ Doc seleccionado' : 'Sin documento'}
                                </span>
                                <span>+</span>
                                <span className={selectedPurchase ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                                    {selectedPurchase ? '✓ Factura seleccionada' : 'Sin factura'}
                                </span>
                                <span>+</span>
                                <span className={selectedBank ? 'text-purple-600 font-medium' : 'text-muted-foreground'}>
                                    {selectedBank ? '✓ Mov. seleccionado' : 'Sin movimiento'}
                                </span>
                            </div>
                            <Button onClick={createManualMatch} disabled={!selectedInbox && !selectedPurchase && !selectedBank}>
                                <Plus className="h-4 w-4 mr-2" />
                                Crear Match
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Editar Propuesta de Asiento</DialogTitle>
                    </DialogHeader>

                    {editedProposal && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium">Descripción</label>
                                    <Input
                                        value={editedProposal.description || ''}
                                        onChange={(e) => setEditedProposal({ ...editedProposal, description: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">Fecha</label>
                                    <Input
                                        type="date"
                                        value={editedProposal.date || ''}
                                        onChange={(e) => setEditedProposal({ ...editedProposal, date: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">Líneas del Asiento</label>
                                <div className="space-y-2">
                                    {editedProposal.entries?.map((entry, i) => (
                                        <div key={i} className="grid grid-cols-4 gap-2">
                                            <Input
                                                placeholder="Código"
                                                value={entry.account_code || ''}
                                                onChange={(e) => updateEntry(i, 'account_code', e.target.value)}
                                            />
                                            <Input
                                                placeholder="Nombre cuenta"
                                                value={entry.account_name || ''}
                                                onChange={(e) => updateEntry(i, 'account_name', e.target.value)}
                                            />
                                            <Input
                                                type="number"
                                                placeholder="Debe"
                                                value={entry.debit || ''}
                                                onChange={(e) => updateEntry(i, 'debit', parseFloat(e.target.value) || 0)}
                                            />
                                            <Input
                                                type="number"
                                                placeholder="Haber"
                                                value={entry.credit || ''}
                                                onChange={(e) => updateEntry(i, 'credit', parseFloat(e.target.value) || 0)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
                        <Button onClick={saveEdit}>Guardar Cambios</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
